import { Children, useMemo, useRef } from 'react';
import { Transition } from 'react-transition-group';
import MojitoCore from 'mojito/core';
import { omit } from 'mojito/utils';

const { cssTimeToMs } = MojitoCore.Base.NumberUtils;
const { hasRenderedSomething } = MojitoCore.Base.ReactUtils;
const log = MojitoCore.logger.get('HideablePane');

const STATE = {
    VISIBLE: 'visible',
    HIDDEN: 'hidden',
};

export default function HideablePane(props) {
    const {
        children,
        mojitoTools: { config },
    } = props;

    checkUnconditionallyRenderedContent(children, config);
    return config.enableTransitions ? <AnimatedContent {...props} /> : <StaticContent {...props} />;
}

const Content = props => {
    const {
        hidden,
        children,
        visibilityState,
        transitionRef,
        mojitoTools: { config, style },
    } = props;
    const shouldUnmount = hidden && !config.renderHiddenContent;
    const containerStyle = style[visibilityState];
    return (
        <div className={'ta-HideablePane'} style={containerStyle} ref={transitionRef}>
            {shouldUnmount ? null : <div style={config.style.innerContainer}>{children}</div>}
        </div>
    );
};

const StaticContent = props => {
    const {
        hidden,
        mojitoTools: { config },
    } = props;

    if (hidden && config.unmountOnHide) {
        return null;
    }
    return <Content {...props} visibilityState={getVisibilityState(hidden)} />;
};

const AnimatedContent = props => {
    const {
        hidden,
        mojitoTools: { config },
    } = props;
    const transitionRef = useRef();
    const transitionDuration = useMemo(() => {
        const stateProp = getVisibilityState(hidden);
        const cssTimeStr = config.style[stateProp].transitionDuration;
        return cssTimeToMs(cssTimeStr);
    }, [hidden, config]);

    return (
        <Transition
            nodeRef={transitionRef}
            in={!hidden}
            timeout={transitionDuration}
            unmountOnExit={config.unmountOnHide}
        >
            {state => {
                // Setting hidden to true forces transition which will take some time.
                // In order to not unmount children immediately we will wait until Transition state 'exited' (which signalizes that transition is done).
                const hideTransitionDone = state === 'exited';
                return (
                    <Content
                        {...props}
                        hidden={hidden && hideTransitionDone}
                        visibilityState={state}
                        transitionRef={transitionRef}
                    />
                );
            }}
        </Transition>
    );
};

HideablePane.getStyle = config => {
    const { base, visible, hidden } = config.style;
    const escapeTransition = style => omit(style, 'transitionProperty', 'transitionDuration');
    const visibleStyle = { ...base, ...visible };
    const hiddenStyle = { ...base, ...hidden };
    return {
        entering: visibleStyle,
        entered: visibleStyle,
        exiting: hiddenStyle,
        exited: hiddenStyle,
        [STATE.VISIBLE]: escapeTransition(visibleStyle),
        [STATE.HIDDEN]: escapeTransition(hiddenStyle),
    };
};

const getVisibilityState = hidden => (hidden ? STATE.HIDDEN : STATE.VISIBLE);
const checkUnconditionallyRenderedContent = (children, config) => {
    if (MojitoCore.Utils.isDebugMode() && config.warnAboutUnconditionallyRenderedContent) {
        Children.forEach(children, child => {
            if (hasRenderedSomething(child)) {
                log.warn(
                    'The following child executes rendering code, even when it is hidden.' +
                        'Consider changing the unconditionally invoked function call(s) to a ' +
                        'Renderable component, to avoid the overhead. Alternatively, disable ' +
                        'the warning by setting the "warnAboutUnconditionallyRenderedContent" ' +
                        'config value to false for this case.',
                    child
                );
            }
        });
    }
};
